import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Redux
 */
import { useSelector } from "react-redux";
import { UseAppDispatch } from "redux/store";
import {
  setShouldRevalidate,
  getCurrentStepKeySelector,
  getShouldRevalidate,
  checkoutStepsSelector,
  setIsExpanded,
  setIsDisabled,
  setCurrentStep,
} from "redux/checkoutSteps";
import { useLazyRevalidateQuery } from "redux/services/revalidate";
import {
  itinerarySelector,
  selectedItinerarySelector,
} from "redux/selectedItinerary";
import { searchQuerySelector } from "redux/searchQuery";
import { searchFormSelector, ISearchForm } from "redux/searchForm";

/**
 * Utils
 */
import { LazyMussa } from "utils/LazyMussa";
import { useTranslations } from "hooks/useTranslations";

/**
 * Components
 */
import { ResponseIconMap } from "components/ResponsePopup/ResponseIconMap";
import { CheckoutSidebar } from "components/Checkout/CheckoutDesktop/CheckoutSidebar/CheckoutSidebar";
import { CheckoutMainContent } from "components/Checkout/CheckoutDesktop/CheckoutMainContent/CheckoutMainContent";
import { CheckoutMobile } from "components/Checkout/CheckoutMobile/CheckoutMobile";
import { MetapartnerTracking } from "components/MetapartnerTracking/MetapartnerTracking";
import {
  MobileHeader,
  MobileCheckoutHeader,
  DesktopHeader,
} from "components/Header";
import { Box } from "@mui/material";

/**
 * Components
 */
import {
  SidebarLayout,
  SlotMachineLoader,
  MediaQuery,
  useConfig,
  CtaDialog,
} from "@thekilroygroup/shared-ui";
import { RevalidateHandler } from "../../components/ResponsePopup/RevalidateHandler";
import { useCancelOnReturnFromPayment } from "../../hooks/useCancelOnReturnFromPayment";
import { IAppConfigObject } from "../../appConfig.interfaces";
import { buildQueryString } from "../../utils/queryStrings";
import { buildSearchObjFromItinerary } from "../../utils/buildSearchObjFromItinerary";

const CheckoutPage = () => {
  const { appConfig } = useConfig();
  useCancelOnReturnFromPayment(appConfig as IAppConfigObject);
  const { translations } = useTranslations();
  const { includesBrandedFares, itineraries, itineraryFromSearch } =
    useSelector(selectedItinerarySelector);

  const itinerary = useSelector(itinerarySelector);
  const { steps, currentStep } = useSelector(checkoutStepsSelector);
  const dispatch = UseAppDispatch();
  const getCurrentStepKey = useSelector(getCurrentStepKeySelector);
  const shouldRevalidate = useSelector(getShouldRevalidate);

  const searchQuery: ISearchForm = useSelector(searchQuerySelector);
  const searchForm: ISearchForm = useSelector(searchFormSelector);
  const navigate = useNavigate();

  const [trigger, revalidateResult] = useLazyRevalidateQuery();
  const { isFetching, data, isError } = revalidateResult;

  const [showRevalidatePopup, setShowRevalidatePopup] = useState(false);
  const navigateToSearchPage = () => {
    navigate(
      `/search-results?${
        itineraryFromSearch &&
        buildQueryString(buildSearchObjFromItinerary(itineraryFromSearch))
      }`,
    );
  };

  useEffect(() => {
    if (shouldRevalidate) {
      trigger({
        itinerary: itineraryFromSearch,
        searchQuery,
        searchRequestId: searchForm.searchRequestId,
        config: appConfig,
      });
    } else {
      dispatch(setShouldRevalidate(true));
    }

    window.scrollTo({
      behavior: "smooth",
      top: 0,
    });

    if (
      includesBrandedFares &&
      steps.brandedFares.disabled &&
      itineraries.length > 1
    ) {
      dispatch(setCurrentStep(0));
      dispatch(setIsExpanded({ key: "brandedFares", expanded: true }));
      dispatch(setIsDisabled({ key: "brandedFares", disabled: false }));
    } else if (
      ((!includesBrandedFares || itineraries.length === 1) &&
        currentStep === 0) ||
      currentStep === 1
    ) {
      dispatch(setCurrentStep(1));
      dispatch(setIsExpanded({ key: "contactInformation", expanded: true }));
      dispatch(setIsDisabled({ key: "contactInformation", disabled: false }));
    }
  }, []);

  useEffect(() => {
    if (data?.changes && !isFetching) {
      setShowRevalidatePopup(true);
    }
  }, [data, isFetching]);

  const getCurrentStepTitle = (): string => {
    type TStepkey =
      | "brandedFares"
      | "contactInformation"
      | "travelerInformation"
      | "additionalProducts"
      | "paymentMethod";
    return translations[getCurrentStepKey as TStepkey].title || "";
  };

  const responseTranslations = translations.customResponseCodes[4006];

  return (
    <>
      {isFetching && (
        <SlotMachineLoader
          title={translations.searchResultsPage.loader.isRevalidatingTitle}
        />
      )}
      {showRevalidatePopup && (
        <>
          <RevalidateHandler
            revalidateResult={revalidateResult}
            confirmAndNavigateTo={() => setShowRevalidatePopup(false)}
            setIsRevalidated={() => {}}
          />
        </>
      )}
      {isError && (
        <CtaDialog
          {...responseTranslations}
          icon={
            (responseTranslations.icon &&
              ResponseIconMap[
                responseTranslations.icon as keyof typeof ResponseIconMap
              ]) ||
            undefined
          }
          onClose={() => {
            navigateToSearchPage();
          }}
          ctaAction={() => {
            navigateToSearchPage();
          }}
        />
      )}

      <Box sx={{ backgroundColor: { xs: "white.main", md: "grey.100" } }}>
        <LazyMussa />
        <MediaQuery max="md">
          <MobileHeader
            top={<MobileCheckoutHeader title={getCurrentStepTitle()} />}
          />
          {itinerary && !isFetching && <CheckoutMobile />}
        </MediaQuery>
        <MediaQuery min="md">
          <DesktopHeader />
          {itinerary && !isFetching && (
            <SidebarLayout
              mainContent={<CheckoutMainContent />}
              mainContentSx={{ py: 3 }}
              sidebar={<CheckoutSidebar />}
              sidebarSx={{
                backgroundColor: "white.main",
              }}
              animated
              alignSidebar="right"
            />
          )}
        </MediaQuery>
      </Box>
      {itinerary && searchQuery.utm_source && (
        <MetapartnerTracking
          selectedItinerary={itinerary}
          eventType="pre-conversion"
          createdDate={new Date().toISOString()}
          utm_source={searchQuery.utm_source}
          conversion_id=""
          searchQuery={searchQuery}
          price={itinerary?.totalFareAmounts.total || 0}
          isDirectBooking={searchForm.isDirectBooking || false}
        />
      )}
    </>
  );
};

export default CheckoutPage;
